// T046: Turn Manager

import type { GameState, BattlefieldPosition, CardDefinition } from '../../types';
import { GameStateMachine, type ActionType } from './GameStateMachine';
import { DrawPhaseHandler } from '../rules/DrawPhaseHandler';
import { SummonPhaseHandler } from '../rules/SummonPhaseHandler';
import { TacticsPhaseHandler } from '../rules/TacticsPhaseHandler';
import { DefensePhaseHandler } from '../rules/DefensePhaseHandler';
import { EndPhaseHandler } from '../rules/EndPhaseHandler';
import { getLogger } from '../../utils/logger';

const log = getLogger('TurnManager');

// Store cardDefinitions for defense phase resolution
let storedCardDefinitions: Map<string, CardDefinition> = new Map();

export type TurnActionResult =
  | { success: true; state: GameState }
  | { success: false; error: string };

/**
 * Execute the draw phase action.
 */
export function executeDraw(state: GameState): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'draw')) {
    return { success: false, error: 'Cannot draw in current phase' };
  }

  const newState = DrawPhaseHandler.execute(state, state.activePlayerId);
  return { success: true, state: newState };
}

/**
 * Execute a summon action.
 */
export function executeSummon(
  state: GameState,
  cardId: string,
  position: BattlefieldPosition
): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'summon')) {
    return { success: false, error: 'Cannot summon in current phase' };
  }

  const result = SummonPhaseHandler.summonUnit(
    state,
    state.activePlayerId,
    cardId,
    position,
    storedCardDefinitions
  );

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Execute a traveler summon action.
 */
export function executeSummonTraveler(
  state: GameState,
  position: BattlefieldPosition
): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'summonTraveler')) {
    return { success: false, error: 'Cannot summon traveler in current phase' };
  }

  const result = SummonPhaseHandler.summonTraveler(state, state.activePlayerId, position);

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Execute an attack declaration.
 */
export function executeAttack(state: GameState, attackerIds: string[]): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'attack')) {
    return { success: false, error: 'Cannot attack in current phase' };
  }

  const result = TacticsPhaseHandler.declareAttack(state, state.activePlayerId, attackerIds);

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Execute a unit move action.
 */
export function executeMove(
  state: GameState,
  from: BattlefieldPosition,
  to: BattlefieldPosition
): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'move')) {
    return { success: false, error: 'Cannot move in current phase' };
  }

  const result = TacticsPhaseHandler.moveUnit(state, state.activePlayerId, from, to);

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Execute a unit swap action (swap two units or move to empty slot).
 */
export function executeSwap(
  state: GameState,
  from: BattlefieldPosition,
  to: BattlefieldPosition
): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'move')) {
    return { success: false, error: 'Cannot swap in current phase' };
  }

  const result = TacticsPhaseHandler.swapUnits(state, state.activePlayerId, from, to);

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Execute a tactical summon (in tactics phase).
 */
export function executeTacticalSummon(
  state: GameState,
  cardId: string,
  position: BattlefieldPosition,
  definitions?: Map<string, CardDefinition>
): TurnActionResult {
  if (!GameStateMachine.canPerformAction(state, 'tacticalSummon')) {
    return { success: false, error: 'Cannot tactical summon in current phase' };
  }

  const result = TacticsPhaseHandler.tacticalSummon(
    state,
    state.activePlayerId,
    cardId,
    position,
    definitions
  );

  if (result.success) {
    return { success: true, state: result.state };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Set card definitions for combat resolution.
 */
export function setCardDefinitions(definitions: Map<string, CardDefinition>): void {
  storedCardDefinitions = definitions;
}

/**
 * End the current phase and transition to the next.
 * Automatically handles phase-specific logic:
 * - Defense phase: resolves combat first
 * - End phase: untaps units, checks game end
 * - Draw phase (after transition): auto-draws cards
 */
export function endPhase(state: GameState, cardDefinitions?: Map<string, CardDefinition>): TurnActionResult {
  try {
    // Guard: Don't process if game has already ended
    if (state.gameEnded) {
      log.debug('endPhase called but game already ended, ignoring');
      return { success: true, state: { ...state } };
    }

    let currentState = state;
    const defs = cardDefinitions ?? storedCardDefinitions;

    log.debug(`endPhase called - current phase: ${currentState.currentPhase}, turn: ${String(currentState.turnNumber)}, active: ${currentState.activePlayerId}`);
    log.debug(`Attack declarations: ${String(currentState.attackDeclarations.length)}`, currentState.attackDeclarations.map(a => a.attackerId));

    // If ending defense phase, resolve combat first
    if (currentState.currentPhase === 'defense') {
      log.debug('Executing defense phase combat resolution...');
      const defenseResult = DefensePhaseHandler.execute(currentState, defs);
      currentState = defenseResult.state;

      // Log combat results
      log.debug(`Combat occurred: ${String(defenseResult.combatOccurred)}`);
      log.debug('Destroyed units:', defenseResult.destroyedUnits);
      log.debug('Combat messages:', defenseResult.messages);
      if (defenseResult.combatOccurred) {
        log.debug('Combat resolved, destroyed units:', defenseResult.destroyedUnits);
      }
    }

    // If ending end phase, execute end phase logic (untap, check game end)
    if (currentState.currentPhase === 'end') {
      const endResult = EndPhaseHandler.execute(currentState, defs);
      currentState = endResult.state;

      // Check if game ended
      if (endResult.gameEnded) {
        log.debug('Game ended! Winner:', endResult.winnerId);
        return { success: true, state: currentState };
      }
    }

    const event = GameStateMachine.getExpectedTransition(currentState);
    let newState = GameStateMachine.transition(currentState, event);

    // Auto-execute draw phase when entering it
    if (newState.currentPhase === 'draw') {
      newState = DrawPhaseHandler.execute(newState, newState.activePlayerId);
      log.debug('Auto-draw executed for', newState.activePlayerId);
    }

    return { success: true, state: newState };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Execute auto-phase actions (like automatic draw at start of turn).
 */
export function executeAutoPhaseActions(state: GameState): GameState {
  let newState = state;

  // Auto-draw at start of draw phase
  if (newState.currentPhase === 'draw') {
    newState = DrawPhaseHandler.execute(newState, newState.activePlayerId);
  }

  return newState;
}

/**
 * Get available actions for the current player.
 */
export function getAvailableActions(state: GameState): ActionType[] {
  return GameStateMachine.getAvailableActions(state);
}

/**
 * Check if the game has ended.
 */
export function isGameEnded(state: GameState): boolean {
  return GameStateMachine.isGameEnded(state);
}

/**
 * Check if it's a specific player's turn.
 */
export function isPlayerTurn(state: GameState, playerId: string): boolean {
  return state.activePlayerId === playerId;
}

/**
 * Get the current phase name in Chinese.
 */
export function getPhaseNameZh(state: GameState): string {
  const phaseNames: Record<string, string> = {
    draw: '抽牌階段',
    defense: '防禦階段',
    summon: '召喚階段',
    tactics: '戰術階段',
    end: '結束階段',
  };
  return phaseNames[state.currentPhase] ?? state.currentPhase;
}

// Re-export as object for backwards compatibility
export const TurnManager = {
  executeDraw,
  executeSummon,
  executeSummonTraveler,
  executeAttack,
  executeMove,
  executeSwap,
  executeTacticalSummon,
  endPhase,
  setCardDefinitions,
  executeAutoPhaseActions,
  getAvailableActions,
  isGameEnded,
  isPlayerTurn,
  getPhaseNameZh,
};
